import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { useForm, FormProvider } from 'react-hook-form';
import { IconCalendar } from 'components/Icons';
import KSListItem from '../KSListItem';
import KSDropdown from '../KSDropdown';
import { Option } from 'types';
import { VendorTemplate } from '@hone-automation/common';
import './APAgingConfigurationModal.scss';
import ApplyButton from '../ReportPLDatesModal/components/ApplyButton';
import { useQueryState } from '../../../hooks/useQueryState';

interface APAgingConfigurationModalProps {
  isModalOpen: boolean;
  handleModalCloseRequest: () => void;
  onSubmit: (data: APAgingConfigurationData) => void;
  onReportChange: (reportType: 'AP Aging' | VendorTemplate, configData?: { dateRange: number }) => void;
  availableConfigurations?: APAgingConfiguration[];
  defaultConfiguration?: string;
  defaultLimit?: number;
}

interface APAgingConfiguration {
  id: string;
  title: string;
  type: string;
  settings: {
    intervals: number[];
  };
}

interface APAgingConfigurationData {
  configurationId: string;
  intervals: number[];
  dateRange: number;
}

const defaultConfigurations: APAgingConfiguration[] = [
  {
    id: 'ap-aging',
    title: 'AP Aging',
    type: 'default',
    settings: {
      intervals: [30, 60, 90],
    },
  },
  {
    id: 'Vendor Invoices',
    title: 'Vendor Invoices',
    type: 'Vendor Invoices',
    settings: {
      intervals: [30, 60, 90],
    },
  },
];

const dateRangeOptions: Option[] = [
  { value: 4, label: '4 Weeks' },
  { value: 8, label: '8 Weeks' },
  { value: 13, label: '13 Weeks' },
];

function APAgingConfigurationModal({
  isModalOpen,
  handleModalCloseRequest,
  onSubmit,
  onReportChange,
  availableConfigurations = defaultConfigurations,
  defaultConfiguration = 'ap-aging',
  defaultLimit = 4,
}: APAgingConfigurationModalProps) {
  const [selectedConfigurationId, setSelectedConfigurationId] = useState<string>(defaultConfiguration);
  const [limitParam, setLimitParam] = useQueryState('limit', defaultLimit.toString());

  const selectedConfiguration = availableConfigurations.find(config => config.id === selectedConfigurationId);

  const getInitialValuesFromURL = (): APAgingConfigurationData => {
    // Read configurationId directly from URL since parent component manages it
    const urlParams = new URLSearchParams(window.location.search);
    const urlConfigId = urlParams.get('configurationId') || defaultConfiguration;

    return {
      configurationId: urlConfigId,
      intervals: selectedConfiguration?.settings.intervals || [30, 60, 90],
      dateRange: limitParam ? parseInt(String(limitParam), 10) : defaultLimit,
    };
  };

  const getDefaultValues = (): APAgingConfigurationData => {
    return {
      configurationId: selectedConfigurationId,
      intervals: selectedConfiguration?.settings.intervals || [30, 60, 90],
      dateRange: defaultLimit,
    };
  };

  const methods = useForm<APAgingConfigurationData>({
    defaultValues: getDefaultValues(),
  });

  const { handleSubmit, watch, reset } = methods;

  const saveConfigurationToURL = (data: APAgingConfigurationData) => {
    // Note: configurationId is managed by the parent component via onReportChange callback
    // We only save the modal-specific parameters here
    setLimitParam(data.dateRange.toString());
  };

  // Initialize form values from URL on component mount
  useEffect(() => {
    if (isModalOpen) {
      const initialValues = getInitialValuesFromURL();
      setSelectedConfigurationId(initialValues.configurationId);
      reset(initialValues);
    }
  }, [isModalOpen, reset, availableConfigurations, defaultConfiguration, defaultLimit, limitParam]);

  const onRequestClose = () => {
    reset(getDefaultValues());
    setSelectedConfigurationId(defaultConfiguration);
    handleModalCloseRequest();
  };

  const handleFormSubmit = (data: APAgingConfigurationData) => {
    // Save configuration to URL when applying changes
    saveConfigurationToURL(data);

    onSubmit(data);

    // Convert the selected configuration to the appropriate type for onReportChange
    const reportType =
      data.configurationId === 'ap-aging' ? ('AP Aging' as const) : (data.configurationId as VendorTemplate);

    // Delay the callbacks to ensure URL is saved first
    setTimeout(() => {
      onReportChange(reportType, { dateRange: data.dateRange });
      onRequestClose();
    }, 100);
  };

  const handleClickListItem = (configuration: APAgingConfiguration) => {
    setSelectedConfigurationId(configuration.id);
    reset({
      ...watch(),
      configurationId: configuration.id,
    });
  };

  const handleDateRangeChange = (option: Option) => {
    reset({
      ...watch(),
      dateRange: option.value,
    });
  };

  return (
    <Modal id="ap-aging-configuration-modal-container" isOpen={isModalOpen} onRequestClose={onRequestClose}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <div className="ap-aging-configuration-modal">
            <div className="modal-header">
              <h3 className="modal-title">
                <IconCalendar /> AP Aging Configuration
              </h3>
              <button type="button" className="close-button" onClick={onRequestClose}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="modal-list">
                {availableConfigurations.map(configuration => {
                  const isSelected = selectedConfigurationId === configuration.id;
                  return (
                    <KSListItem
                      key={configuration.id}
                      label={configuration.title}
                      isSelected={isSelected}
                      onClickListItem={() => handleClickListItem(configuration)}
                    />
                  );
                })}
              </div>
              <div className="modal-content">
                {selectedConfigurationId === 'ap-aging' ? (
                  <>
                    <div className="modal-options-check">
                      <div className="modal-subsection-title">Configuration Options</div>
                      <div className="modal-subsection">
                        <div className="modal-subsection-content">
                          <p className="configuration-unavailable-message">
                            Report configurations are not available for selected report type.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="modal-subsection">
                      <div className="modal-subsection-title">Aging Intervals</div>
                      <div className="modal-subsection-content">
                        <p>Current intervals: {watch('intervals').join(', ')} days</p>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="modal-subsection">
                      <div className="modal-subsection-title">Report Settings</div>
                      <div className="modal-subsection-content">
                        <KSDropdown
                          options={dateRangeOptions}
                          value={
                            dateRangeOptions.find(option => option.value === watch('dateRange')) || dateRangeOptions[0]
                          }
                          label="Date Range"
                          theme="grey"
                          onSelect={handleDateRangeChange}
                        />
                      </div>
                    </div>
                    {/* Pending confirmation if it is going to be used or not */}
                    {/*<div className="modal-options-check">*/}
                    {/*  <div className="modal-subsection-title">Configuration Options</div>*/}
                    {/*  <KSCheckBox*/}
                    {/*    checked={watch('showZeroBalances')}*/}
                    {/*    onChange={checked => reset({ ...watch(), showZeroBalances: checked })}*/}
                    {/*    label="Show Zero Balances"*/}
                    {/*  />*/}
                    {/*</div>*/}
                  </>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <ApplyButton disabled={false} />
            </div>
          </div>
        </form>
      </FormProvider>
    </Modal>
  );
}

export default APAgingConfigurationModal;
